import { UpdateActions } from '@actions/universalTrackerValue';
import { SURVEY } from '@constants/terminology';
import { UtrvVariationWarning } from '@g17eco/types/question';
import { VARIATION_DATA_SOURCE_LABEL_MAP } from '@utils/universalTracker';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

const { VERIFY, UPDATE, REJECT } = UpdateActions.STATUS;

const CONFIRMATION_TEXT = {
  [UPDATE]: 'Are you sure you want to submit this value?',
  [VERIFY]: 'Are you sure you want to verify this submission?',
};

const CONFIRMATION_ACTION = {
  [UPDATE]: 'Submit',
  [VERIFY]: 'Verify',
  [REJECT]: 'Reject',
};

const getParagraphPrefix = (action: string) => {
  if (action === VERIFY) {
    return 'You are about to verify a value';
  }
  return 'The value entered';
};

const generateTableConfirmationText = (variationWarnings: UtrvVariationWarning[]) => {
  const firstReportingDate = variationWarnings[0].baselineReportingDate;
  const isSameReportingDate = variationWarnings.every((item) => item.baselineReportingDate === firstReportingDate);
  if (isSameReportingDate) {
    return (
      <span>
        Some of the values entered for this metric fall outside of the variance limit set by the admin in the{' '}
        {firstReportingDate} {SURVEY.SINGULAR}.
      </span>
    );
  }
  return (
    <>
      <span>Some of the values entered for this metric fall outside of the variance limit set by the admin:</span>
      <ul className='mt-2'>
        {variationWarnings.map((warning, index) => {
          const { formattedBaseline, baselineReportingDate, dataSource } = warning;
          return (
            <li key={index}>
              The baseline value for this metric was {formattedBaseline} set in {baselineReportingDate} (
              {VARIATION_DATA_SOURCE_LABEL_MAP[dataSource]}).
            </li>
          );
        })}
      </ul>
    </>
  );
};

export interface VariationConfirmModalProps {
  action: string;
  handleClose: () => void;
  handleSubmit: () => void;
  variationWarnings: UtrvVariationWarning[];
}

export const VariationConfirmModal = (props: VariationConfirmModalProps) => {
  const { variationWarnings, handleClose, handleSubmit, action } = props;

  if (!variationWarnings || variationWarnings.length === 0) {
    return null;
  }

  const { valueListCode, formattedBaseline, formattedCurrentInput, baselineReportingDate, variance } =
    variationWarnings[0];

  const onSubmit = () => {
    handleSubmit();
    handleClose();
  };

  return (
    <Modal isOpen={!!action} toggle={handleClose} backdrop='static'>
      <ModalHeader toggle={handleClose}>Variation detected</ModalHeader>
      <ModalBody>
        <div>
          <strong>Warning: </strong>
          {valueListCode ? (
            generateTableConfirmationText(variationWarnings)
          ) : (
            <span>
              {getParagraphPrefix(action)} <strong>{formattedCurrentInput}</strong> falls outside the {variance}%
              variance limit set by the admin. The baseline value for this metric was{' '}
              <strong>{formattedBaseline}</strong> set in {baselineReportingDate}.
            </span>
          )}
        </div>
        <div className='mt-3'>{CONFIRMATION_TEXT[action]}</div>
      </ModalBody>
      <ModalFooter>
        <Button color='transparent' onClick={handleClose}>
          Cancel
        </Button>
        <Button color='primary' onClick={onSubmit}>
          {CONFIRMATION_ACTION[action]}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
